import type { EChartsOption } from 'echarts'

import { formatDecimal } from '@/lib/format'
import { messages } from '@/lib/messages'
import type { CashVariance } from '@/services/http/cash'

type Category = CashVariance['categories'][number]
type Cari = CashVariance['caris'][number]
type Accuracy = CashVariance['accuracy'][number]

const t = messages.cash.variance

/** Vurgu eşiği varsayılanı (%): yalnız görünüm tercihi, iş kuralı değil, kaydedilmez. */
export const DEFAULT_THRESHOLD_PCT = 20
const MAX_THRESHOLD_PCT = 1000

/** URL'deki `esik`: sonlu ve 0 ile 1000 arasında değilse varsayılan. */
export function parseThreshold(raw: string | undefined): number {
  if (raw === undefined || raw.trim() === '') return DEFAULT_THRESHOLD_PCT
  const value = Number(raw.trim().replace(',', '.'))
  return Number.isFinite(value) && value >= 0 && value <= MAX_THRESHOLD_PCT
    ? value
    : DEFAULT_THRESHOLD_PCT
}

/** Sunucunun işareti: tahmin − gerçekleşen. */
export function diffOf(item: { forecast: number; actual: number }): number {
  return item.forecast - item.actual
}

/** Eşik %'sinin altındaki fark "yok" sayılır; yalnız sıfıra yakınlığı (kuruş) eler. */
const EPSILON = 0.005

export type DiffDirection = 'forecast_higher' | 'actual_higher' | 'equal'

/** Yön tarafsız söylenir: hangisinin büyük olduğu, iyi ya da kötü demeden. */
export function diffDirection(diff: number): DiffDirection {
  if (diff > EPSILON) return 'forecast_higher'
  if (diff < -EPSILON) return 'actual_higher'
  return 'equal'
}

/** Sapma yüzdesi eşiği aşıyor mu (mutlak değer, eşik dahil). `null` sapma vurgulanmaz. */
export function exceedsThreshold(deviationPct: number | null, thresholdPct: number): boolean {
  return deviationPct != null && Math.abs(deviationPct) >= thresholdPct
}

export interface VarianceSummary {
  /** Kategori farklarının mutlak değerlerinin toplamı (işaretler birbirini götürmesin). */
  absoluteTotal: number
  topCategory: { key: Category['key']; diff: number; deviationPct: number | null } | null
  topCari: { code: string; name: string | null; diff: number } | null
  /** Eşiği aşan kategori sayısı ve toplam kategori sayısı. */
  exceeding: number
  categoryCount: number
}

/** Üst özet: tamamı ekrandaki yanıttan türetilir, sunucuda yeni hesap yok. */
export function summarizeVariance(
  categories: readonly Category[],
  caris: readonly Cari[],
  thresholdPct: number,
): VarianceSummary {
  const topCategory = categories.reduce<Category | null>(
    (best, c) => (best === null || Math.abs(diffOf(c)) > Math.abs(diffOf(best)) ? c : best),
    null,
  )
  const topCari = caris.reduce<Cari | null>(
    (best, c) => (best === null || Math.abs(diffOf(c)) > Math.abs(diffOf(best)) ? c : best),
    null,
  )
  return {
    absoluteTotal: categories.reduce((sum, c) => sum + Math.abs(diffOf(c)), 0),
    topCategory:
      topCategory && Math.abs(diffOf(topCategory)) > EPSILON
        ? {
            key: topCategory.key,
            diff: diffOf(topCategory),
            deviationPct: topCategory.deviation_pct,
          }
        : null,
    topCari:
      topCari && Math.abs(diffOf(topCari)) > EPSILON
        ? { code: topCari.code, name: topCari.name, diff: diffOf(topCari) }
        : null,
    exceeding: categories.filter((c) => exceedsThreshold(c.deviation_pct, thresholdPct)).length,
    categoryCount: categories.length,
  }
}

export type AccuracyMetric = 'mape' | 'bias'

export interface AccuracySeries {
  /** Ufuklar (hafta), artan. */
  horizons: number[]
  /** Kategori başına ufuk sırasıyla değer; ölçüm yoksa `null`. */
  series: Array<{ category: string; values: Array<number | null> }>
}

/** Doğruluk ölçümlerini ufuk ekseni ve kategori serilerine çevirir. */
export function accuracySeries(
  accuracy: readonly Accuracy[],
  metric: AccuracyMetric,
): AccuracySeries {
  const horizons = [...new Set(accuracy.map((a) => a.horizon))].sort((a, b) => a - b)
  const categories = [...new Set(accuracy.map((a) => a.category))]
  return {
    horizons,
    series: categories.map((category) => ({
      category,
      values: horizons.map((h) => {
        const hit = accuracy.find((a) => a.category === category && a.horizon === h)
        return hit ? (metric === 'mape' ? hit.mape : hit.bias) : null
      }),
    })),
  }
}

/** Fark metni: işaretli tutar ve tarafsız yön ("tahmin fazla"), renksiz. */
export function signedDiff(diff: number): string {
  const sign = diff > 0 ? '+' : ''
  return `${sign}${formatDecimal(diff, 0)}`
}

const num = (v: number) => formatDecimal(v, 0)
const label = (key: string) => t.kinds[key] ?? key

/** Kategori bazında tahmin ve gerçekleşen yan yana; ipucunda fark (tahmin − gerçekleşen) ve sapma %. */
export function categoryChartOption(categories: readonly Category[]): EChartsOption {
  return {
    grid: { left: 8, right: 8, top: 36, bottom: 8, containLabel: true },
    legend: { top: 0, data: [t.forecast, t.actual] },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (raw) => {
        const first = Array.isArray(raw) ? raw[0] : raw
        const c = categories[first?.dataIndex ?? -1]
        if (!c) return ''
        const pct = c.deviation_pct == null ? '–' : `${formatDecimal(c.deviation_pct, 1)}%`
        return [
          `<b>${label(c.key)}</b>`,
          `${t.forecast}: ${num(c.forecast)}`,
          `${t.actual}: ${num(c.actual)}`,
          `${t.diffShort}: ${num(diffOf(c))} (${pct})`,
        ].join('<br/>')
      },
    },
    xAxis: { type: 'category', data: categories.map((c) => label(c.key)) },
    yAxis: { type: 'value', axisLabel: { formatter: (v: number) => num(v) } },
    series: [
      { name: t.forecast, type: 'bar', data: categories.map((c) => c.forecast) },
      { name: t.actual, type: 'bar', data: categories.map((c) => c.actual) },
    ],
  }
}

/** Ufuk (kaç hafta önceden) ekseninde kategori başına MAPE ya da yanlılık çizgisi. */
export function accuracyChartOption(
  accuracy: readonly Accuracy[],
  metric: AccuracyMetric,
): EChartsOption {
  const { horizons, series } = accuracySeries(accuracy, metric)
  const unit = metric === 'mape' ? '%' : ''
  const fmt = (v: number) => `${formatDecimal(v, metric === 'mape' ? 1 : 0)}${unit}`
  return {
    grid: { left: 8, right: 16, top: 36, bottom: 24, containLabel: true },
    legend: { top: 0, data: series.map((s) => label(s.category)) },
    tooltip: { trigger: 'axis', valueFormatter: (v) => (typeof v === 'number' ? fmt(v) : '–') },
    xAxis: {
      type: 'category',
      name: t.horizon,
      nameLocation: 'middle',
      nameGap: 28,
      data: horizons.map(String),
    },
    yAxis: { type: 'value', axisLabel: { formatter: (v: number) => fmt(v) } },
    series: series.map((s) => ({
      name: label(s.category),
      type: 'line' as const,
      connectNulls: false,
      symbolSize: 7,
      data: s.values,
    })),
  }
}
